import React , { useState, useCallback, useMemo, useContext} from 'react';
import { Grid } from '../../../shared/grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Autocomplete, Tooltip, TextField, OutlinedInput, Box, FormControl, RadioGroup, FormControlLabel, Radio, Typography, Button, Switch, Checkbox } from '@mui/material';
import Chip from '@mui/material/Chip';
import { FireDangerSelectDownload, FireDangerFileFormatHourly,FireDangerFileFormatDaily, hours, FuelModels, } from '../../../constants';
import CloseIcon from '@mui/icons-material/Close';
import '../Download.scss';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment-timezone';
import {  isValidDate } from 'src/store/common';
import { useFormikContext } from 'formik';
import InfoIcon from '@mui/icons-material/Info';
import { List } from 'react-virtualized';
import dayjs, { Dayjs } from 'dayjs';

type ScrollCtxValue ={
    scrollTopRef: React.MutableRefObject<number>;
    restoreToken: number;
};

const ScrollCtx = React.createContext<ScrollCtxValue | null>(null);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const VirtualizedListBox = React.forwardRef<HTMLDivElement, any>(
    function ListboxComponent(props, ref) {
        const { children, selected, ...other } = props;
        const itemData = useMemo(() => children?.map((child: any) => child.props.value), [children]);
        const allProps = useMemo(() => children?.map((child: any) => child.props), [children]);
        const scrollCtx = useContext(ScrollCtx);
        const listRef = React.useRef<any>(null);
        const onScroll = useCallback(({ scrollTop }: { scrollTop: number }) => {
            if (scrollCtx) {
                scrollCtx.scrollTopRef.current = scrollTop;
            }
        }, [scrollCtx]);

        React.useLayoutEffect(() => {
            if (!scrollCtx) return;
            if (!listRef.current) return;
            listRef.current.scrollToPosition(scrollCtx.scrollTopRef.current);

        }, [scrollCtx]);


        const rowRenderer = React.useCallback((data: any) => {
            return ({ index, key, style }) => {
                return (
                    <Row index={index} key={key} style={style} data={data} />
                )
            }
        }, []);

        return (
            <div ref={ref} role={'button'}
                tabIndex={0}
              onKeyDown={other.onMouseDown}
              onMouseDown={other.onMouseDown}
              onClick={other.onMouseDown}>
                <List
                    height={400}
                    maxHeight={400}
                    rowCount={itemData.length}
                    rowHeight={50}
                    width={665}
                    rowRenderer={rowRenderer(allProps)}
                    onScroll={onScroll}
                    {...other}
                />

            </div>
        )
    }
);
    

interface RowProps {
    index:number,
    key: string,
    style: React.CSSProperties,
    data: any
  }
  
  const Row: React.FC<RowProps> = React.memo(({ index, key, style, data }) => {
    const option = data[index];
    const isSelectAll = option.value === 'select-all';
    // Check if it's "Select All" and if it should be checked
    const isChecked = isSelectAll
      ? data.every((opt) => opt['aria-selected'] || opt.value === 'select-all') 
      : option['aria-selected'];
  
    return (
            <div key = { key } style = { style } 
                {...option}
            >
                <Checkbox
                    id={key}
                    color="success"
                    checked={isChecked}
                />
                { option.value.search_name }
            </div >

    );
  });

type FireDangerDLProps = {
    fireDangerDLParams: any,
    clearSelected:any,
    onPresetSelected: (presetSelected: boolean) => void
}
export const FireDanger: React.FC<FireDangerDLProps> = ({fireDangerDLParams, clearSelected, onPresetSelected}) => {
    const { values, errors, touched, setFieldTouched, setFieldValue} = useFormikContext<any>();
    const [errorStartDate, setErrorStartDate] = useState<string|null>(null);
    const [errorEndDate, setErrorEndDate] = useState<string|null>(null);
    const [presetSelected, setPresetSelected] = useState(false);

    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const [filteredStations, setFilteredStations] = useState<any[]>([]);

    const [inputValue, setInputValue] = useState('');
    
    const stylebutton = presetSelected ? { backgroundColor: '#012954' }:{}
    const scrollTopRef = React.useRef(0);
    const [restoreToken, setRestoreToken] = useState(0)

    // helper validators (return error string or null)
    const validateStart = (start: Dayjs | null, end: Dayjs | null, vals: any) => {
        if (!start) return "Start date is required";
        // forecast requires start >= today
        if (vals.dataSet === "forecast" && start.isBefore(fireDangerDLParams.today(), "day")) {
         return "Start date cannot be in the past for forecasts";
        }
        // if end exists, ensure start < end
        if (end && !start.isBefore(end, "day") && !start.isSame(end, "day")) {
            return "Start date must be before end date";
        }
        return null;
    };

    const validateEnd = (end: Dayjs | null, start: Dayjs | null, vals: any) => {
        if (!end) return "End date is required";
        if (start && !end.isAfter(start, "day") && !end.isSame(start, "day")) {
            return "End date must be after start date";
        }
        if ((vals.dataSet === "forecast") && vals.dateTimeIncrements === "daily") {
            if (start && end.isBefore(start, "day")) {
                return "Forecast unavailable for days in the past";
            }
        }
        return null;
    };

    const onStartChange = (newValue:Date|null) => {
        const currentValues = values;
        const newStart = newValue ? dayjs(newValue).startOf('day') : null;
        const currentEnd = dayjs(moment(values.endDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).utc().toDate());

        const startError = validateStart(newStart, currentEnd, currentValues);
        const endError = validateEnd(currentEnd, newStart, currentValues);

        setFieldValue('startDate', moment(moment(newStart?.toDate()).utc().toDate()).format("YYYY-MM-DD"));
        if(startError) {
            setErrorStartDate(startError);
            fireDangerDLParams.setErrorDate('Invalid Date');
        }
        else {
            setFieldTouched("startDate", true);
            setErrorStartDate('');
            fireDangerDLParams.setErrorDate('');
        }

        if(endError) {
            setErrorEndDate(endError);
            fireDangerDLParams.setErrorDate('Invalid Date');
        }
        else {
            setErrorEndDate('');
            fireDangerDLParams.setErrorDate('');
        }
    }
    const onEndChange = (newValue:Date|null) => {
        const currentValues = values;
        const newEnd = newValue ? dayjs(newValue).startOf("day") : null;
        const currentStart = dayjs(moment(values.startDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).utc().toDate());

        const endError = validateEnd(newEnd, currentStart, currentValues);
        const startError = validateStart(currentStart, newEnd, currentValues);

        setFieldValue("endDate", moment(moment(newEnd?.toDate()).utc().toDate()).format("YYYY-MM-DD"));
        if (endError) {
            setErrorEndDate(endError);
            fireDangerDLParams.setErrorDate('Invalid Date')
        }
        else {
            setFieldTouched("endDate", true);
            setErrorEndDate('');
            fireDangerDLParams.setErrorDate('');
        }

        if (startError) {
            setErrorStartDate(startError);
            fireDangerDLParams.setErrorDate('Invalid Date');
        }
        else {
            setErrorStartDate('');
            fireDangerDLParams.setErrorDate('');
        }
    };

    const handleTimeIncrements = (e:any)=>{ 
        fireDangerDLParams.setDataIncrements(e);
    };

    return (
        <div>
            {/* FireDanger SELECTIION */}
            <Grid row desktop={{ col: 6 }} className={values.dataSubject === 'FireDanger' ? '' : 'hideRow'}>
                <label id='fuelModels'>Fuel Model(s) <span className='required'>Required</span>
                    <Select
                        multiple
                        value={fireDangerDLParams.fuelModels}
                        id= 'fuelModels'
                        input={<OutlinedInput label="Tag"/>}
                        onChange={fireDangerDLParams.handleFuelModels}
                        MenuProps={MenuProps}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value: any) => (
                               value!=='All' && 
                                    <Chip key={value} 
                                        label={value}
                                        onMouseDown={(event) => {
                                            event.stopPropagation();
                                        }}
                                        onKeyDown={(event)=>{event.key==="Backspace" && fireDangerDLParams.deleteFuelModels(value) }}
                                        onDelete={()=>{fireDangerDLParams.deleteFuelModels(value)}}
                                        deleteIcon={<CloseIcon tabIndex={0} onKeyDown={(event) => { event.key==='Enter' && fireDangerDLParams.deleteFuelModels(value)}}/>}
                                    />
                              ))}
                            </Box>
                        )}
                        error={!!errors.fuelModels && !!touched.fuelModels }                        

                    >
                        {FuelModels.map((fm) => (
                            <MenuItem
                            key={fm}
                            value={fm}
                            >
                            {fm}
                            </MenuItem>
                        ))}
                    </Select>
                </label>
                <span style={{color: '#d32f2f'}}>{touched.fuelModels && errors.fuelModels}</span>
            </Grid>
            <Grid row desktop={{ col: 6 }} className={values.dataSubject === 'FireDanger' ? '' : 'hideRow'}>
                <label id='dataSet'>Data Set <span className='required'>Required</span>
                    <Select
                        displayEmpty
                        renderValue={values.dataSet !== "" ? undefined : () => "Select a Data Set"}
                        labelId='dataSet'
                        name="dataSet"
                        className= "dataDownload"
                        onBlur={()=>setFieldTouched('dataSet')}
                        onChange={e => { 
                            fireDangerDLParams.setDataSet(e) 
                            setPresetSelected(false); 
                            onPresetSelected(false); 
                        }}
                        label="Select a Data Set"
                        value={values.dataSet}
                        aria-disabled={values.dataSubject === ''}
                        error={!!errors.dataSet && !!touched.dataSet }                        
                        placeholder={"Select a Data Set"}
                    >
                        {
                            FireDangerSelectDownload.map((FireDangerOption: any) => (
                                <MenuItem value={FireDangerOption.value} key={FireDangerOption.value}>{FireDangerOption.label}</MenuItem>
                            ))
                        }
                    </Select>
                </label>
                <span style={{color: '#d32f2f'}}>{touched.dataSet && errors.dataSet}</span>
            </Grid>
            <Grid row className={(values.dataSubject===('FireDanger') ? '' : 'hideRow')} desktop={{ col: 6 }}>
                <label htmlFor = 'DateTimeFrequency' >Date Time Frequency <span className='required'>Required</span>
                <div style={{ margin: '5px 0', fontSize: 'medium' }}>
                    <div className={"font-sans-sm "} style={{ paddingTop: '10px', paddingBottom: '10px', fontWeight: 'normal' }}>
                        <FormControl>
                            <RadioGroup
                                id="fireDangerIncrements" 
                                name="controlled-radio-buttons-group"
                                value={values.dataTimeIncrements}
                                onChange={(event) => { handleTimeIncrements(event); }}
                                onBlur={fireDangerDLParams.handleBlur}
                            >
                                <FormControlLabel 
                                    key={`daily`} 
                                    value={'daily'} 
                                    control={<Radio size='small' style={{padding: '5px'}}/>} 
                                        label={<Typography style={{fontSize: '1rem'}}>Daily</Typography>} 
                                />
                                <FormControlLabel 
                                    key={`hourly`} 
                                    value={'hourly'} 
                                    control={<Radio size='small' style={{padding: '5px'}}/>} 
                                        label={<Typography style={{fontSize: '1rem'}}>Hourly</Typography>} 
                                />
                            </RadioGroup>
                        </FormControl>
                    </div>
                </div>
                </label>
                <span style={{ color: '#d32f2f' }}>{errors.dataTimeIncrements && touched.dataTimeIncrements && errors.dataTimeIncrements}</span>
                {values.dataTimeIncrements === 'daily' && (
                    <div style={{ paddingLeft: '1.5%', fontSize: '16px', width: '100%', paddingBottom: '1.5%' }} className="selectedStationTextBox">
                        (Daily) Maximum/Minimum data will be downloaded from midnight to midnight local station time.
                    </div>
                )}
            </Grid>
            <Grid row desktop={{ col: 6 }}>
                <label id='dataFormat'>Data Format <span className='required'>Required</span>
                    <Select
                        displayEmpty
                        renderValue={values.dataFormat !== "" ? undefined : () => "Select a Data Format"}
                        name="dataFormat"
                        onBlur={()=>setFieldTouched('dataFormat')}
                        error={!!errors.dataFormat && !!touched.dataFormat }                        
                        className= "dataDownload"
                        onChange={fireDangerDLParams.setDataFormat}
                        label="Select Data Format"
                        value={values.dataTimeIncrements ==='hourly'? `${values.dataFormat}-${values.timeZoneType}`: values.dataFormat}
                        placeholder='Select a Data Format'
                        aria-disabled={values.dataSubject === '' || values.dataSet === '' || values.dataTimeIncrements===''}
                    >
                        {
                            values.dataTimeIncrements ==='hourly' && FireDangerFileFormatHourly.map((fileType: any) => (
                                <MenuItem value={fileType.value} key={fileType.value}>{fileType.label}</MenuItem>
                            ))       
                        }
                        {
                            values.dataTimeIncrements ==='daily' && FireDangerFileFormatDaily.map((fileType: any) => (
                                <MenuItem value={fileType.value} key={fileType.value}>{fileType.label}</MenuItem>
                            ))       
                        }
                    </Select>
                </label>
                <span style={{color: '#d32f2f'}}>{errors.dataFormat && touched.dataFormat && errors.dataFormat}</span>
                {values.dataSubject.includes('FireDanger') && values.timeZoneType === 'utc' && (
                    <div style={{ paddingLeft: '1.5%', fontSize: '16px', width: '100%', paddingBottom: '1.5%' }} className="selectedStationTextBox">
                    Time is in UTC
                    </div>
                )}
                {values.dataSubject.includes('FireDanger') && values.timeZoneType === 'lst' && (
                    <div style={{ paddingLeft: '1.5%', fontSize: '15px', width: '100%', paddingBottom: '1.5%' }} className="selectedStationTextBox">
                    Time is adjusted to time zone of station but not adjusted for daylight savings.
                    </div>
                )}
            </Grid>
            <Grid row className={values.dataSubject===('FireDanger') ? '' : 'hideRow'} desktop={{ col: 8 }}>
                <div><span className='font-sans-md text-bold'>Date Range</span> <span className='required1'>Required</span>
                    <div className={`date-range-picker-wrapper ${fireDangerDLParams.errorDate? 'error':''}`} style={{ margin: '10px 0' }}>
                        <DatePicker
                            label="Start Date"
                            disabled={presetSelected}
                            disablePast={values.dataTimeIncrements==='daily' && values.dataSet==='forecast' && values.dataSubject==='FireDanger'}
                            value={moment(values.startDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).utc().toDate()}                         
                            onChange={onStartChange}
                            onError={(reason, value) => {
                                if (reason === 'invalidDate' || value === null || !isValidDate(value)) {
                                    setErrorStartDate('Invalid Start Date');
                                    fireDangerDLParams.setErrorDate('Invalid Date');
                                }
                                else if(reason === 'disablePast') {
                                    setErrorStartDate('Daily Forecast unavailable for previous dates');
                                    fireDangerDLParams.setErrorDate('Invalid Date');
                                }
                                else if(reason){
                                    setErrorStartDate('An Error on Start Date: ' + reason);
                                    fireDangerDLParams.setErrorDate('Invalid Date');
                                }
                                else{
                                    setErrorStartDate('');
                                    if(errorEndDate === '') {
                                        fireDangerDLParams.setErrorDate('');
                                    }
                                }
                            }}
                            slotProps={{
                                textField: {
                                    error: errorStartDate ? true : false,
                                    helperText: errorStartDate,
                                    id: 'uploadStartDate',
                                },
                            }}
                        />
                        <span style={{ margin: '0 10px' }}></span>
                        <DatePicker
                            label="End Date"
                            disabled={presetSelected}
                            disablePast={values.dataTimeIncrements==='daily' && values.dataSet==='forecast' && values.dataSubject==='FireDanger'}
                            value={moment(values.endDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).utc().toDate()}
                            aria-labelledby='uploadEndDate'
                            onChange={onEndChange}                        
                            onError={(reason, value) => {
                                if (reason === 'invalidDate' || value === null || !isValidDate(value)) {
                                    setErrorEndDate('Invalid End Date');
                                    fireDangerDLParams.setErrorDate('Invalid Date');
                                }
                                else if(reason === 'disablePast') {
                                    setErrorEndDate('Daily Forecast unavailable for previous dates');
                                    fireDangerDLParams.setErrorDate('Invalid Date');
                                }
                                else if(reason){
                                    setErrorEndDate('An Error on End Date: ' + reason);
                                    fireDangerDLParams.setErrorDate('Invalid Date');
                                }
                                else{
                                    setErrorEndDate('');
                                    if(errorStartDate === '') {
                                        fireDangerDLParams.setErrorDate('');
                                    }
                                }
                            }}
                            slotProps={{
                                textField: {
                                    error: errorEndDate ? true : false,
                                    helperText: errorEndDate,
                                    id: 'uploadEndDate',

                                },
                            }}
                        />
                    </div>
                </div>
            </Grid>
            <span style={{color: '#d32f2f'}}>{fireDangerDLParams.errorDate}</span>
            {
                (values.dataTimeIncrements === 'hourly') &&
                <Grid row className={(values.dataTimeIncrements === 'daily' || values.dataSubject === 'FireDanger') ? 'hideRow' : ''} gap="md">
                <Grid desktop={{ col: 2 }}>
                    <i className="fas fa-clock" />

                    <label id='startHr'>
                            <Tooltip title="This is the hour that your observations will begin">
                                <span>Start Hour <InfoIcon/></span>
                            </Tooltip>
                            <Select
                                disabled={presetSelected}
                                displayEmpty
                                onBlur={fireDangerDLParams.handleBlur}
                                renderValue={values.startHour !== "" ? undefined : () => "00:00"}
                                labelId='startHour'
                                name="startHour"
                                onChange={fireDangerDLParams.setStartHour}
                                label="startHour"
                                value={values.startHour}
                                className="select-styles special dataDownload"
                                placeholder='00:00'
                                id="startHour"
                            >
                                {hours.map((hr: any) => (
                                    <MenuItem value={hr.value} key={`starthour - ${hr.value}`}>{hr.label}</MenuItem>
                                ))}
                            </Select>
                        </label>
                </Grid>
                <Grid desktop={{ col: 2 }}>
                    <i className="fas fa-clock" />

                    <label id='endHr'>
                            <Tooltip title="This is the hour that your observations will end">
                                <span>End Hour <InfoIcon/></span>
                            </Tooltip>
                        <Select
                            disabled={presetSelected}
                            displayEmpty
                            onBlur={fireDangerDLParams.handleBlur}
                            renderValue={values.endHour !== "" ? undefined : () => "23:00"}
                            labelId='endHour'
                            name="endHour"
                            onChange={fireDangerDLParams.setEndHour}
                            label="Select EndHour"
                            value={values.endHour}
                            className="select-styles special dataDownload"
                            placeholder='23:00'
                            id="endHour"
                        >
                            {hours.map((hr: any) => (
                                <MenuItem value={hr.value} key={`endhour - ${hr.value}`}>{hr.label}</MenuItem>
                            ))}
                        </Select>
                    </label>
                </Grid>
            </Grid>
            }
            <Grid row className={(!values.dataFormat.includes("csv") || values.dataSubject !== 'FireDanger') || !values.dataTimeIncrements.includes("hourly") ? 'hideRow' : ''} style={{ paddingBottom: '15px' }}>
                <span style={{ fontStyle: "italic" }}> Forecast data may be incomplete on day seven</span>
            </Grid>
            {/* CONDITIONAL BUTTON */}
            {values.dataSet === "observationforecast" && (
                        <Grid row desktop={{ col: 6 }} style={{ marginTop: "15px", marginBottom: "15px" }}>
                        <Button
                            variant="contained"
                            color="primary"
                            style={stylebutton}
                            onClick={() => {
                                fireDangerDLParams.setLinkGenerated(false);
                                setPresetSelected((prev) => !prev); 
                                const isPresetSelected = !presetSelected;
                                onPresetSelected(isPresetSelected); 
                                const startDate = moment().subtract(5, 'days').utc().format('YYYY-MM-DD');
                                setFieldValue('startDate', startDate);

                                if (presetSelected) {
                                  const endDate = moment().add(7, 'days').utc().format('YYYY-MM-DD');
                                  setFieldValue('endDate', endDate);
                                }
                                else{
                                    const endDate = moment().add(6, 'days').utc().format('YYYY-MM-DD');
                                    setFieldValue('endDate', endDate);
                                }
                              }}
                        >
                            Previous 5 Days and 7-Day Forecast
                        </Button>
                        </Grid>
                    )}
                    {values.dataSubject === 'FireDanger' &&
                    values.dataTimeIncrements === 'daily' &&
                    values.dataFormat !== '' && (
                        <Grid row desktop={{ col: 6 }} style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={values.averageFireDanger}
                                        onChange={(e) => {
                                            fireDangerDLParams.setLinkGenerated(false);
                                            setFieldValue('averageFireDanger', e.target.checked);
                                        }}
                                        size="medium"
                                        color="primary"
                                        inputProps={{ 'aria-label': 'Average Fire Danger Outputs toggle' }}
                                    />
                                }
                                label="Average Fire Danger Outputs"
                            />
                        </Grid>
                    )}
            <Grid row className={values.dataSubject === '' ? 'hideRow' : ''} desktop={{ col: 6 }}>
                <div style={{width: '100%'}}><label className='font-sans-md text-bold' htmlFor='selectedStations'>Selected Station(s)<span className='required1'>Required</span>
                    <div className='searchStationsDownloadPage'>
                        <i className="fas fa-search" />
                        <ScrollCtx.Provider value ={{scrollTopRef, restoreToken}}>

                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                noOptionsText={
                                    <div className='no-results'>
                                        <div>No results match your search criteria.</div>
                                        <div><b>Here are a few tips:</b></div>
                                        <div>• Remember to check your spelling<br />
                                            • Ensure you are searching either a Station Name or Station ID number</div>
                                    </div>
                                }
                                className="dataDownload"
                                inputValue={inputValue}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (reason === 'input') {
                                        setInputValue(newInputValue);
                                        const search = newInputValue.toLowerCase();
                                        const filtered = fireDangerDLParams?.stationsForSearch.filter((station:any) => {
                                            if (search.length <= 2 && search.length !== 0) {
                                                return station.state?.toLowerCase().includes(search);
                                            }
                                            return (
                                                station.station_name?.toLowerCase().includes(search) ||
                                                station.network_name?.toLowerCase().includes(search) ||
                                                station.state?.toLowerCase().includes(search)
                                            );
                                        });
                                        if(fireDangerDLParams?.currSelectedStations?.length>0){
                                            setFilteredStations([...fireDangerDLParams.currSelectedStations, ...filtered]);
                                        }
                                        else{
                                            setFilteredStations(filtered);
                                        }
                                    }
                                }}
                                onChange={(e, value) => {
                                    const isSelectAllCurrentlyChecked = selectAllChecked;
                                    const isSelectAllStillSelected = value.some((item) => item.value === 'select-all');
                                    const isFiltered = inputValue.trim().length > 0;
                                    const stationsToSelect = isFiltered ? filteredStations : fireDangerDLParams?.stationsForSearch;
                                    if (isSelectAllCurrentlyChecked && !isSelectAllStillSelected) {
                                        fireDangerDLParams.handleSearch(e, []);
                                        setSelectAllChecked(false);
                                        return;
                                    }
                                    if (!isSelectAllCurrentlyChecked && isSelectAllStillSelected) {
                                        const allWithSelectAll = [...stationsToSelect];
                                        if (!isFiltered) {
                                            allWithSelectAll.unshift({
                                                station_name: 'Select All',
                                                network_name: 'Select All',
                                                value: 'select-all',
                                                search_name: 'Select All',
                                            });
                                        }
                                        fireDangerDLParams.handleSearch(e, allWithSelectAll);
                                        setSelectAllChecked(!isFiltered);
                                        setInputValue('');
                                        setFilteredStations(fireDangerDLParams?.stationsForSearch);
                                        return;
                                    }
                                    if (isSelectAllCurrentlyChecked && isSelectAllStillSelected) {
                                        fireDangerDLParams.handleSearch(e, value);
                                        setSelectAllChecked(false);
                                        return;
                                    }
                                    fireDangerDLParams.handleSearch(e, value);
                                    const allSelected = value.length === fireDangerDLParams?.stationsForSearch.length;
                                    const noFilter = inputValue.trim().length === 0;
                                    setSelectAllChecked(allSelected && noFilter);
                                    setRestoreToken((t) => t + 1);
                                }}
                                value={fireDangerDLParams.valsFromSearch}
                                id="selectedStations"
                                onBlur={()=>setFieldTouched('dataSelectedStations')}
                                isOptionEqualToValue={(option,value)=>option?.search_name===value?.search_name}
                                options={fireDangerDLParams?.stationsForSearch? [{station_name:'Select All', network_name: 'Select All', value: 'select-all',search_name: 'Select All', type: `${values.dataTimeIncrements}`,download:`weather` },...fireDangerDLParams.stationsForSearch]:[]}
                                renderOption={
                                    (props, option)=>(
                                    <li {...props} key={option.search_name} value={option} id={option}>
                                        {option.search_name}
                                    </li>
                                )}
                                getOptionLabel={(option)=>`${option.search_name}`}
                                filterOptions={(options, {inputValue})=>{
                                    const search = inputValue.toLowerCase();
                                    if(search.length<=2 && search.length!==0){
                                        return options.filter((option)=>
                                            option.state?.toLowerCase().includes(search) || 
                                            option.station_name==='Select All' || 
                                            option.station_id.toString().includes(search)
                                        )
                                    }
                                    return options.filter((option)=>
                                        option.station_name?.toLowerCase().includes(search) ||
                                        option.network_name?.toLowerCase().includes(search) ||
                                        option.state?.toLowerCase().includes(search) ||
                                        option.station_name==='Select All' ||
                                        option.station_id.toString().includes(search)
                                    )
                                }}   
                                ListboxComponent={VirtualizedListBox}                          
                                renderInput={(params) => (
                                    <TextField
                                        placeholder='Search All Stations...'
                                        className="dataDownload"
                                        aria-label={'Search Station...'}
                                        data-testid={'search-bar'}
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            type: 'search',
                                        }}
                                        helperText ={!!touched.dataSelectedStations && !!errors.dataSelectedStations? errors.dataSelectedStations: ''}

                                        FormHelperTextProps={{
                                            tabIndex:0
                                        }}
                                        aria-errormessage='Station(s) are required'
                                        onBlur={()=>setFieldTouched('dataSelectedStations')}
                                        error={!!touched.dataSelectedStations && !!errors.dataSelectedStations}
                                    />
                                )}
                            />
                        </ScrollCtx.Provider>

                    </div>
                    </label>

                </div>
                    {
                    values.dataTimeIncrements ==='hourly' && values.dataFormat!=='zip' && 
                    <div className={'selectedStationTextBox'} style={{ marginTop: '10%', width: '75vw!important'}}>
                        <ul>
                            <li>Up to 300 stations and 1 fuel model can be selected for requests that are greater than or equal to 20-years worth of data</li>
                            <li>Up to 150 stations and 2 fuel model can be selected for requests that are greater than or equal to 20-years worth of data</li>
                            <li>Up to 100 stations and 3 fuel model can be selected for requests that are greater than or equal to 20-years worth of data</li>
                            <li>Up to 75 stations and 4 fuel model can be selected for requests that are greater than or equal to 20-years worth of data</li>
                            <li>Up to 60 stations and 5 fuel model can be selected for requests that are greater than or equal to 20-years worth of data</li>
                            <li>If you are using the URL from the Copy Data Link to load Fire Danger Data with multiple fuel models into Excel or in the read-only API, the data will not load if you included more than 20 stations with dates that are greater than the preset dates (Previous 5 Days and 7 Day Forecast)..</li>
                        </ul>
                    </div>
                    }
                    {
                    values.dataTimeIncrements ==='hourly' && values.dataFormat==='zip' && 
                    <div className={'selectedStationTextBox'} style={{ marginTop: '10%', width: '75vw!important'}}>
                        <ul>
                            <li>Up to 50 stations and 1 fuel model can be selected for requests that are greater than or equal to 20-years worth of data</li>
                            <li>Up to 25 stations and 2 fuel model can be selected for requests that are greater than or equal to 20-years worth of data</li>
                            <li>Up to 16 stations and 3 fuel model can be selected for requests that are greater than or equal to 20-years worth of data</li>
                            <li>Up to 13 stations and 4 fuel model can be selected for requests that are greater than or equal to 20-years worth of data</li>
                            <li>Up to 10 stations and 5 fuel model can be selected for requests that are greater than or equal to 20-years worth of data</li>
                            <li>If you are using the URL from the Copy Data Link to load Fire Danger Data with multiple fuel models into Excel or in the read-only API, the data will not load if you included more than 20 stations with dates that are greater than the preset dates (Previous 5 Days and 7 Day Forecast)..</li>
                        </ul>
                    </div>
                    }
                {
                      
                      selectAllChecked ? (
                        <div className={values.dataSelectedStations ? 'selectedStations' : 'hideRow'}>
                            <div> {`All stations have been selected. Total stations selected: ${fireDangerDLParams.currSelectedStations?.length-1}`}</div>
                            <div><button type='button' onClick={fireDangerDLParams.clearSelected}>Clear All</button></div>

                        </div>
                        ): (
                          <div className={values.dataSelectedStations ? 'selectedStations' : 'hideRow'}>
                          <div>{`These are your selected stations. Total stations selected: ${fireDangerDLParams.currSelectedStations?.length}`}</div>
                          <div className="smooth-scrollbar" style={{maxHeight: '400px', border: '1px solid #717171', width: '75vw',padding: '3px', borderRadius:'5px'}}>
                              {
                            fireDangerDLParams.currSelectedStations.map((station) =>
                                <Chip 
                                    label={`${station.station_name}, ${station.network_name}, ${station.state}, ${station.station_id}`}
                                    key={station.station_id} 
                                    onDelete={() => fireDangerDLParams.handleDelete(station.station_id)} 
                                    onKeyDown={(event)=>{event.key==="Backspace" && fireDangerDLParams.handleDelete(station.station_id) }}
                                    variant="outlined" 
                                    deleteIcon={<CloseIcon tabIndex={0} onKeyDown={(event) => { event.key==='Enter' && fireDangerDLParams.handleDelete(station.station_id)}}/>} 
                                />
                                        )

                                    }
                                </div>
                                <div><button type='button' onClick={clearSelected}>Clear All</button></div>
                            </div>  
                                        )
                                    }
            </Grid>    

        </div>
    )
}
